import { defineRecipe } from '@plurall/elo-v3';

export const performanceByCompetenceRootRecipe = defineRecipe({
  className: 'performance-table',
  base: {
    width: '100%',

    // As barras e os dots da legenda são divs com background-color, que o
    // Chrome descarta na impressão por padrão — sem isto a tabela sai sem barras.
    _print: {
      printColorAdjust: 'exact',
      '& *': {
        printColorAdjust: 'exact',
      },
    },
  },
});

export const performanceByCompetenceEmptyRecipe = defineRecipe({
  className: 'performance-table__empty',
  base: {
    margin: 0,
    color: 'text.secondary',
  },
});

export const performanceByCompetenceTableRecipe = defineRecipe({
  className: 'performance-table__table',
  base: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    marginBlockEnd: 'space.24',
  },
});

export const performanceByCompetenceHeaderRowRecipe = defineRecipe({
  className: 'performance-table__header-row',
  base: {
    backgroundColor: 'background.secondary',
    borderRadius: 'shape.8',
    overflow: 'hidden',
  },
});

export const performanceByCompetenceHeaderCellRecipe = defineRecipe({
  className: 'performance-table__header-cell',
  base: {
    fontSize: 'font.size.xsmall',
    color: 'foreground.tertiary',
    fontWeight: 'font.weight.semibold',
    textAlign: 'left',
    paddingBlock: 'space.8',
    paddingInline: 'space.16',
    borderBottom: 'none',
  },
  variants: {
    position: {
      first: {
        borderTopLeftRadius: 'shape.8',
        borderBottomLeftRadius: 'shape.8',
      },
      middle: {
        padding: 0,
      },
      last: {
        width: '1%',
        textAlign: 'right',
        borderTopRightRadius: 'shape.8',
        borderBottomRightRadius: 'shape.8',
      },
    },
  },
});

export const performanceByCompetenceConceptsRecipe = defineRecipe({
  className: 'performance-table__concepts',
  base: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingBlock: 'space.8',
    paddingInline: 'space.16',
    gap: 'space.4',
  },
});

export const performanceByCompetenceConceptRecipe = defineRecipe({
  className: 'performance-table__concept',
  base: {
    fontSize: 'font.size.large',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    position: 'relative',
    color: 'foreground.tertiary',
    '&:first-child::before, &::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '1px',
      height: '60%',
      backgroundColor: 'border.tertiary',
    },
    '&:first-child::before': {
      left: 'calc(var(--elo-spacing-space-4) * -1)',
    },
    '&::after': {
      right: 'calc(var(--elo-spacing-space-4) * -1)',
    },
  },
});

export const performanceByCompetenceRowRecipe = defineRecipe({
  className: 'performance-table__row',
  base: {
    height: '3rem',
    maxHeight: '3rem',
    // Na coluna mais estreita do papel os critérios quebram em duas linhas.
    _print: { height: 'auto', maxHeight: 'none' },
  },
});

export const performanceByCompetenceCellRecipe = defineRecipe({
  className: 'performance-table__cell',
  base: {
    borderBottom: '1px solid {colors.border.tertiary}',
    verticalAlign: 'middle',
    height: '3rem',
    maxHeight: '3rem',
    _print: { height: 'auto', maxHeight: 'none' },
  },
  variants: {
    kind: {
      criterion: {
        fontSize: 'font.size.small',
        color: 'foreground.primary',
        paddingInline: 'space.16',
        width: '150px',
      },
      bars: {
        paddingInline: 'space.16',
      },
      averages: {
        paddingInline: 'space.16',
        textAlign: 'right',
      },
    },
  },
});

export const performanceByCompetenceBarsContainerRecipe = defineRecipe({
  className: 'performance-table__bars-container',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
    justifyContent: 'center',
  },
});

export const performanceByCompetenceBarRecipe = defineRecipe({
  className: 'performance-table__bar',
  base: {
    width: '100%',
    height: '10px',
    borderRadius: 'shape.4',
    transition: 'width var(--timing-normal) ease',
  },
  variants: {
    kind: {
      media: {
        backgroundColor: 'foreground.brand',
      },
      ultima: {
        backgroundColor: 'feedback.information.standard',
      },
    },
  },
});

export const performanceByCompetenceAveragesValuesRecipe = defineRecipe({
  className: 'performance-table__averages-values',
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
});

export const performanceByCompetenceAverageValueRecipe = defineRecipe({
  className: 'performance-table__average-value',
  base: {
    margin: 0,
    fontSize: 'font.size.small',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: '10px',
    gap: '4px',
    color: 'foreground.primary',
    fontWeight: 500,
  },
  variants: {
    kind: {
      media: {
        color: 'text.brand',
      },
      ultima: {
        color: 'feedback.information.stronger',
      },
    },
  },
});

export const performanceByCompetenceLegendRecipe = defineRecipe({
  className: 'performance-table__legend',
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'space.24',
  },
});

export const performanceByCompetenceLegendItemRecipe = defineRecipe({
  className: 'performance-table__legend-item',
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: 'space.4',
  },
});

export const performanceByCompetenceLegendDotRecipe = defineRecipe({
  className: 'performance-table__legend-dot',
  base: {
    width: '0.75rem',
    height: '0.75rem',
    borderRadius: '50%',
    flexShrink: 0,
  },
  variants: {
    kind: {
      media: {
        backgroundColor: 'foreground.brand',
      },
      ultima: {
        backgroundColor: 'feedback.information.standard',
      },
    },
  },
});

export const performanceByCompetenceLegendTextRecipe = defineRecipe({
  className: 'performance-table__legend-text',
  base: {
    margin: 0,
    fontSize: 'font.size.small',
    display: 'inline-flex',
    color: 'foreground.secondary',
  },
});
